import { useTransition, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardPreview,
  Image,
  Text,
  Content,
  Footer,
  AvatarGroup,
  Skeleton,
} from "@react-spectrum/s2";
import { usePress, useHover } from "@react-aria/interactions";
import { mergeProps } from "@react-aria/utils";
import { useNavigate } from "react-router";

import { PersonaAvatar } from "@app/components/PersonaAvatar";

import { getVisualUrl } from "@app/utils";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const StoriesListCard = ({ storyId, story, UNSAFE_style }) => {
  const navigate = useNavigate();

  const { pressProps, isPressed } = usePress({
    onPress: () => {
      navigate(`/stories/${storyId}`);
    },
  });
  const { hoverProps, isHovered } = useHover({});

  const [isThumbnailPending, startThumbnailTransition] = useTransition();
  const [thumbnailUrl, setThumbnailUrl] = useState();

  useEffect(() => {
    startThumbnailTransition(async () => {
      const url = await getVisualUrl(story?.thumbnail);
      setThumbnailUrl(url);
    });
  }, [story?.thumbnail]);

  return (
    <div
      className={style({ flexGrow: 0, flexShrink: 1, flexBasis: 0 })}
      style={UNSAFE_style}
    >
      <Card
        textValue={story?.title ?? "title"}
        variant="quiet"
        styles={style({ width: "100%", minWidth: 180 })}
      >
        <CardPreview
          UNSAFE_className={style({
            willChange: "transform",
            transform: {
              default: "none",
              isPressed: "perspective(62px) translate3d(0, 0, -2px)",
            },
          })({ isPressed })}
        >
          <Skeleton isLoaded={!isThumbnailPending}>
            <Image
              src={thumbnailUrl}
              alt={story?.title ?? "title"}
              styles={style({
                size: "full",
                objectFit: "cover",
                aspectRatio: "16/9",
              })}
            />
          </Skeleton>
          <div
            {...mergeProps(hoverProps, pressProps)}
            className={style({
              size: "full",
              position: "absolute",
              top: 0,
              left: 0,
              borderRadius: "lg",
              outlineWidth: 2,
              outlineOffset: -2,
              outlineStyle: "solid",
              outlineColor: {
                default: "transparent",
                isHovered: "gray-600",
              },
              transition: "all",
            })({ isHovered })}
          />
        </CardPreview>
        <Content styles={style({ alignContent: "start" })}>
          <Text slot="title">{story?.title ?? "title"}</Text>
          <Text slot="description">
            {story?.storyOverview ?? story?.industry}
          </Text>
        </Content>
        <Footer>
          <AvatarGroup aria-label="Story Personas">
            {story?.personas?.map((persona, index) => (
              <PersonaAvatar key={index} persona={persona} />
            ))}
          </AvatarGroup>
        </Footer>
      </Card>
    </div>
  );
};

StoriesListCard.propTypes = {
  storyId: PropTypes.string.isRequired,
  story: PropTypes.object,
  UNSAFE_style: PropTypes.object,
};
